import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Megaphone, ChevronRight, Eye, Ban } from 'lucide-react';
import CenteredStatusPopup from '@/components/merchant/CenteredStatusPopup';
import {
  apiMerchantCampaigns,
  apiMerchantCampaignDetail,
  apiMerchantCancelCampaign,
} from '@/services/api';
import { useUiLanguage } from '@/lib/i18n';
import { AI_ACTION_TYPES, executeAIAction } from '@/lib/aiActionEngine';

const STATUS_TABS = [
  { key: 'all', label: 'All Campaigns' },
  // { key: 'active', label: 'Active' },
  // { key: 'scheduled', label: 'Scheduled' },
  { key: 'completed', label: 'Completed' },
];

const STATUS_STYLE = {
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
  completed: 'bg-stone-200 text-stone-700 border-stone-300',
  cancelled: 'bg-amber-100 text-amber-700 border-amber-200',
};

const PREBUILT_CAMPAIGNS = {
  occasion_based: [
    { subtype: 'birthday_campaign', description: 'Send birthday offer.' },
    { subtype: 'customer_anniversary', description: 'Send anniversary offer.' },
    { subtype: 'restaurant_anniversary', description: 'Send restaurant celebration offer.' },
    { subtype: 'festival_campaign', description: 'Send festive offer.' },
  ],
  behavioural: [
    { subtype: 'win_back', description: 'Bring back inactive guests.' },
    { subtype: 'visit_booster', description: 'Get guests to visit again.' },
    { subtype: 'high_frequency_reward', description: 'Thank frequent guests.' },
  ],
};

const TOGGLE_CAMPAIGNS = [
  { id: 'welcome_bonus', title: 'Welcome Bonus', description: 'First visit reward with configurable spend guard.', helper: 'Config: Min $10 / Max $120 spend' },
  { id: 'second_visit_double', title: '2nd Visit Double Points', description: 'Double points on second visit within selected window.', helper: 'Config: Valid within 7 days' },
  { id: 'third_visit_lockin', title: '3rd Visit Lock-In', description: 'Bonus reward on the third successful visit.', helper: 'Config: Bonus +100 points on visit #3' },
];

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '-';
  }
}

export default function CampaignsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useUiLanguage();

  const [statusTab, setStatusTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState('');
  const [visibleCampaignRows, setVisibleCampaignRows] = useState(25);
  const [payload, setPayload] = useState({ campaigns: [], counts: {}, subtype_meta: {}, default_templates: {} });
  const [highlightedSubtype, setHighlightedSubtype] = useState('');
  const [toast, setToast] = useState({ message: '', type: 'success' });
  const campaignCacheRef = useRef({});
  const [toggles, setToggles] = useState({
    welcome_bonus: true,
    second_visit_double: true,
    third_visit_lockin: true,
  });

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);

  const campaigns = payload?.campaigns || [];
  const visibleCampaigns = campaigns.slice(0, visibleCampaignRows);
  const counts = payload?.counts || {};
  const subtypeMeta = payload?.subtype_meta || {};
  const audienceCounts = useMemo(() => payload?.audience_counts || {}, [payload]);
  const SIMPLE_CAMPAIGN_TITLES = useMemo(
    () => ({
      birthday_campaign: t('Birthday Offer'),
      customer_anniversary: t('Anniversary Offer'),
      restaurant_anniversary: t('Restaurant Celebration'),
      festival_campaign: t('Festival Offer'),
      win_back: t('Come Back Offer'),
      visit_booster: t('Visit Again Offer'),
      high_frequency_reward: t('VIP Thank You Offer'),
    }),
    [t]
  );

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    window.setTimeout(() => setToast({ message: '', type: 'success' }), 3800);
  };

  const invalidateCampaignCache = useCallback(() => {
    campaignCacheRef.current = {};
  }, []);

  const fetchCampaigns = useCallback(async (silent = false, forceRefresh = false) => {
    if (!silent) setLoading(true);
    const cacheKey = statusTab || 'all';
    const cached = campaignCacheRef.current?.[cacheKey];
    if (!forceRefresh && cached && Date.now() - cached.ts < 45000) {
      setPayload(cached.data);
      if (!silent) setLoading(false);
      return;
    }
    try {
      const data = await apiMerchantCampaigns(statusTab);
      const next = data || { campaigns: [], counts: {}, subtype_meta: {}, default_templates: {}, audience_counts: {}, campaign_rules: {} };
      setPayload(next);
      campaignCacheRef.current[cacheKey] = { ts: Date.now(), data: next };
    } catch {
      showToast(t('Unable to load campaign history right now.'), 'error');
    }
    if (!silent) setLoading(false);
  }, [statusTab, t]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    setVisibleCampaignRows(25);
  }, [statusTab, campaigns.length]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      fetchCampaigns(true, true);
    }, 25000);
    return () => window.clearInterval(timer);
  }, [fetchCampaigns]);

  useEffect(() => {
    const prefill = location?.state?.campaignPrefill;
    const subtype = String(prefill?.campaign_subtype || '').trim();
    if (!subtype) return;
    setHighlightedSubtype(subtype);
    showToast(t('Recommended campaign highlighted. Click Run Campaign to execute.'));
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.key]);

  const aiSuggestedCampaigns = useMemo(() => {
    const base = [
      { subtype: 'win_back', title: t('Bring Back At-Risk Guests') },
      { subtype: 'visit_booster', title: t('Increase Repeat Visits') },
      { subtype: 'birthday_campaign', title: t('Send Birthday Wishes') },
      { subtype: 'high_frequency_reward', title: t('Thank Loyal Guests') },
    ];
    return base
      .map((row) => ({ ...row, audience: Number(audienceCounts?.[row.subtype] || 0) }))
      .sort((a, b) => b.audience - a.audience)
      .slice(0, 4);
  }, [audienceCounts, t]);

  const runCampaign = async (subtype) => {
    const subtypeKey = String(subtype || '').trim();
    if (!subtypeKey) return;

    const audience = Number(audienceCounts?.[subtypeKey] || 0);
    if (!audience) {
      showToast(t('No eligible audience found for this campaign yet.'), 'error');
      return;
    }

    setBusyAction(`run-${subtypeKey}`);
    try {
      const res = await executeAIAction(AI_ACTION_TYPES.RUN_CAMPAIGN, {
        campaign_subtype: subtypeKey,
        audience_count: audience,
      });
      showToast(res?.message || t('Campaign executed successfully.'));
      invalidateCampaignCache();
      await fetchCampaigns(true, true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      showToast(typeof detail === 'string' ? detail : t('Unable to run campaign right now.'), 'error');
    }
    setBusyAction('');
  };

  const openDetails = async (campaignId) => {
    if (!campaignId) return;
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const res = await apiMerchantCampaignDetail(campaignId);
      setDetailData(res);
    } catch {
      setDetailData(null);
      showToast(t('Unable to load campaign details.'), 'error');
    }
    setDetailLoading(false);
  };

  const cancelCampaign = async (campaign) => {
    if (!campaign?.id || campaign?.status !== 'scheduled') return;
    setBusyAction(`cancel-${campaign.id}`);
    try {
      await apiMerchantCancelCampaign(campaign.id, t('Cancelled by merchant'));
      showToast(t('Scheduled campaign cancelled.'));
      invalidateCampaignCache();
      await fetchCampaigns(true, true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      showToast(typeof detail === 'string' ? detail : t('Unable to cancel campaign.'), 'error');
    }
    setBusyAction('');
  };

  return (
    <div className="space-y-5" data-testid="merchant-campaigns-page">
      <CenteredStatusPopup message={toast.message} type={toast.type} />

      <div>
        <h1 className="text-4xl font-bold tracking-tight text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
          {t('Campaigns')}
        </h1>
        <p className="text-sm text-stone-500">{t('Choose a campaign and run it in one click.')}</p>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-4">
        <p className="text-sm font-semibold text-stone-800">{t('AI Suggested Campaigns')}</p>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
          {aiSuggestedCampaigns.map((item) => (
            <div key={item.subtype} className="rounded-xl border border-stone-200 bg-stone-50 p-3">
              <p className="text-sm font-semibold text-stone-800">{item.title}</p>
              <button
                type="button"
                onClick={() => runCampaign(item.subtype)}
                disabled={busyAction === `run-${item.subtype}` || item.audience <= 0}
                className="mt-2 h-9 px-3 rounded-lg border border-brand/25 bg-brand-soft text-brand text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
              >
                {busyAction === `run-${item.subtype}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Megaphone className="w-3.5 h-3.5" />}
                {t('Run Campaign')} ({item.audience} {t('Customers')})
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-4">
        <p className="text-sm font-semibold text-stone-800">{t('Toggle Campaigns')}</p>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
          {TOGGLE_CAMPAIGNS.map((item) => (
            <div key={item.id} className="rounded-xl border border-stone-200 bg-stone-50 p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs font-semibold text-stone-800">{t(item.title)}</p>
                <button
                  type="button"
                  onClick={() => setToggles((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                  className={`h-6 px-2 rounded-md text-[10px] font-bold ${toggles[item.id] ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-600'}`}
                >
                    {toggles[item.id] ? t('ON') : t('OFF')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-stone-100 p-4 opacity-80">
        <p className="text-sm font-semibold text-stone-700">{t('AI Auto-Campaigns — Coming Soon')}</p>
        <p className="text-xs text-stone-500 mt-1">{t('Smart campaign orchestration will auto-launch based on churn, spend dips, and weather context.')}</p>
      </div>

      <div className="space-y-3">
        <div>
          <p className="text-sm font-semibold text-stone-800">{t('Occasion-Based')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            {PREBUILT_CAMPAIGNS.occasion_based.map((item) => {
              const subtype = item.subtype;
              const label = SIMPLE_CAMPAIGN_TITLES[subtype] || subtypeMeta?.[subtype]?.label || subtype;
              const audience = Number(audienceCounts?.[subtype] || 0);
              const isHighlighted = highlightedSubtype === subtype;
              return (
                <motion.div
                  key={subtype}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`rounded-2xl border bg-white p-4 ${isHighlighted ? 'border-brand shadow-md shadow-brand/15' : 'border-stone-200'}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-stone-800">{label}</p>
                    {isHighlighted ? <Badge className="rounded-full bg-brand-soft text-brand border border-brand/20">{t('Recommended')}</Badge> : null}
                  </div>
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => runCampaign(subtype)}
                      disabled={busyAction === `run-${subtype}` || audience <= 0}
                      className="h-9 px-3 rounded-lg border border-brand/25 bg-brand-soft text-brand text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                    >
                      {busyAction === `run-${subtype}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Megaphone className="w-3.5 h-3.5" />}
                      {t('Run Campaign')} ({audience} {t('Customers')}) <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <div>
          <p className="text-sm font-semibold text-stone-800">{t('Behavioural')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            {PREBUILT_CAMPAIGNS.behavioural.map((item) => {
              const subtype = item.subtype;
              const label = SIMPLE_CAMPAIGN_TITLES[subtype] || subtypeMeta?.[subtype]?.label || subtype;
              const audience = Number(audienceCounts?.[subtype] || 0);
              const isHighlighted = highlightedSubtype === subtype;
              return (
                <motion.div
                  key={subtype}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`rounded-2xl border bg-white p-4 ${isHighlighted ? 'border-brand shadow-md shadow-brand/15' : 'border-stone-200'}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-stone-800">{label}</p>
                    {isHighlighted ? <Badge className="rounded-full bg-brand-soft text-brand border border-brand/20">{t('Recommended')}</Badge> : null}
                  </div>
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => runCampaign(subtype)}
                      disabled={busyAction === `run-${subtype}` || audience <= 0}
                      className="h-9 px-3 rounded-lg border border-brand/25 bg-brand-soft text-brand text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                    >
                      {busyAction === `run-${subtype}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Megaphone className="w-3.5 h-3.5" />}
                      {t('Run Campaign')} ({audience} {t('Customers')}) <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-100">
          <p className="text-sm font-semibold text-stone-800">{t('Campaign Activity')}</p>
        </div>

        <div className="px-4 pt-3 flex flex-wrap gap-2">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setStatusTab(tab.key)}
              className={`h-9 px-3 rounded-lg text-sm font-semibold border ${statusTab === tab.key ? 'bg-brand-soft border-brand/30 text-brand' : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'}`}
            >
              {t(tab.label)} {typeof counts?.[tab.key] === 'number' ? `(${counts[tab.key]})` : ''}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-stone-100">
                {['Campaign', 'Audience', 'Status', 'Sent', 'Delivered', 'Date', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-widest font-bold text-stone-400">{t(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 3 }).map((_, idx) => (
                  <tr key={idx}><td colSpan={7} className="px-4 py-4"><div className="h-4 rounded bg-stone-100 animate-pulse" /></td></tr>
                ))
              ) : campaigns.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-stone-500">{t('No campaigns found for this filter.')}</td></tr>
              ) : visibleCampaigns.map((campaign) => (
                <tr key={campaign.id} className="border-b border-stone-100 hover:bg-stone-50/60">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-stone-800">{campaign.campaign_name || campaign.campaign_label}</p>
                    {/* <p className="text-xs text-stone-500 mt-0.5">{campaign.campaign_label}</p> */}
                    {campaign.campaign_code ? <p className="text-[11px] text-stone-500 mt-0.5">{t('Code')}: <span className="font-semibold text-stone-700">{campaign.campaign_code}</span></p> : null}
                  </td>
                  <td className="px-4 py-3">{Number(campaign.audience_count || 0).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <Badge className={`rounded-full border ${STATUS_STYLE[campaign.status] || STATUS_STYLE.completed}`}>{campaign.status || 'completed'}</Badge>
                  </td>
                  <td className="px-4 py-3">{Number(campaign.sent_count || 0).toLocaleString()}</td>
                  <td className="px-4 py-3">{Number(campaign.delivered_count || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs text-stone-600">{formatDate(campaign.sent_at || campaign.scheduled_for || campaign.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={() => openDetails(campaign.id)}
                        className="h-8 px-2 rounded-lg border border-stone-200 bg-white text-xs font-semibold inline-flex items-center gap-1"
                      >
                        <Eye className="w-3.5 h-3.5" /> {t('View')}
                      </button>
                      {campaign.status === 'scheduled' ? (
                        <button
                          type="button"
                          onClick={() => cancelCampaign(campaign)}
                          disabled={busyAction === `cancel-${campaign.id}`}
                          className="h-8 px-2 rounded-lg border border-stone-200 bg-white text-xs font-semibold inline-flex items-center gap-1"
                        >
                          {busyAction === `cancel-${campaign.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />} {t('Cancel')}
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && campaigns.length > visibleCampaignRows ? (
            <div className="px-4 pb-4">
              <button
                type="button"
                onClick={() => setVisibleCampaignRows((prev) => prev + 25)}
                className="h-9 px-3 rounded-lg border border-stone-200 bg-white text-sm font-semibold"
              >
                {t('Load More Campaigns')}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="w-[96vw] max-w-5xl max-h-[88vh] overflow-y-auto rounded-2xl" data-testid="campaign-detail-modal">
          <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>{t('Campaign Details')}</DialogTitle>
          <DialogDescription className="text-sm text-stone-500">{t('Delivery analytics and recipient status for this campaign.')}</DialogDescription>

          {detailLoading && <div className="h-52 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>}
          {!detailLoading && !detailData && <p className="text-sm text-stone-500 py-6">{t('Unable to load campaign details.')}</p>}
          {!detailLoading && detailData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <div className="rounded-xl border border-stone-200 p-3"><p className="text-[11px] text-stone-500 uppercase tracking-wider">{t('Targeted')}</p><p className="text-2xl font-bold mt-1">{Number(detailData?.analytics?.total_users_targeted || 0).toLocaleString()}</p></div>
                <div className="rounded-xl border border-stone-200 p-3"><p className="text-[11px] text-stone-500 uppercase tracking-wider">{t('Redemptions')}</p><p className="text-2xl font-bold mt-1">{Number(detailData?.analytics?.redemptions || 0).toLocaleString()}</p></div>
                <div className="rounded-xl border border-stone-200 p-3"><p className="text-[11px] text-stone-500 uppercase tracking-wider">{t('Conversion')}</p><p className="text-2xl font-bold mt-1">{Number(detailData?.analytics?.conversion_rate || 0).toFixed(1)}%</p></div>
                <div className="rounded-xl border border-stone-200 p-3"><p className="text-[11px] text-stone-500 uppercase tracking-wider">{t('Revenue')}</p><p className="text-2xl font-bold mt-1">${Number(detailData?.analytics?.revenue_generated || 0).toFixed(2)}</p></div>
                <div className="rounded-xl border border-stone-200 p-3"><p className="text-[11px] text-stone-500 uppercase tracking-wider">{t('Campaign Visits')}</p><p className="text-2xl font-bold mt-1">{Number(detailData?.analytics?.business_generated_visits || 0).toLocaleString()}</p></div>
                <div className="rounded-xl border border-stone-200 p-3"><p className="text-[11px] text-stone-500 uppercase tracking-wider">{t('New Users')}</p><p className="text-2xl font-bold mt-1">{Number(detailData?.analytics?.new_users_from_campaign || 0).toLocaleString()}</p></div>
              </div>
              <div className="rounded-xl border border-stone-200 overflow-hidden">
                <div className="px-3 py-2 border-b border-stone-100 bg-stone-50">
                  <p className="text-sm font-semibold">{t('Delivery Log')}</p>
                </div>
                <div className="max-h-[360px] overflow-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b border-stone-100">
                        {['Phone', 'Status', 'Provider SID', 'Error', 'Time'].map((h) => (
                          <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-widest font-bold text-stone-400">{t(h)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {!detailData?.deliveries?.length ? (
                        <tr><td colSpan={5} className="px-3 py-6 text-center text-stone-500">{t('No delivery records available yet.')}</td></tr>
                      ) : detailData.deliveries.map((row) => (
                        <tr key={row.id} className="border-b border-stone-100">
                          <td className="px-3 py-2">{row.phone || '-'}</td>
                          <td className="px-3 py-2">
                            <Badge className={`rounded-full border ${row.status === 'delivered' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-rose-100 text-rose-700 border-rose-200'}`}>{row.status ? t(row.status) : t('unknown')}</Badge>
                          </td>
                          <td className="px-3 py-2 text-xs text-stone-600">{row.provider_sid || '-'}</td>
                          <td className="px-3 py-2 text-xs text-stone-600">{row.error || '-'}</td>
                          <td className="px-3 py-2 text-xs text-stone-600">{formatDate(row.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

    </div>
  );
}
